const clonePosition = position => [position[0], position[1], position[2]];

const cloneInterval = interval => ({
  start: clonePosition(interval.start),
  end: clonePosition(interval.end),
});

const annotationInterval = annotation => ({
  start: [
    annotation.startMeasure,
    annotation.startBeat,
    annotation.startSubdivision || 0,
  ],
  end: [
    annotation.endMeasure,
    annotation.endBeat,
    annotation.endSubdivision || 0,
  ],
});

const relevantAnnotations = (annotations, trackId, excludeId) =>
  annotations.filter(annotation => (
    annotation.trackId === trackId && annotation.id !== excludeId
  ));

const failed = (reason, originalInterval) => ({
  success: false,
  reason,
  interval: cloneInterval(originalInterval),
});

export const comparePositions = (left, right) => {
  const leftTuple = [left[0], left[1], left[2] || 0];
  const rightTuple = [right[0], right[1], right[2] || 0];

  for (let index = 0; index < leftTuple.length; index += 1) {
    if (leftTuple[index] < rightTuple[index]) return -1;
    if (leftTuple[index] > rightTuple[index]) return 1;
  }
  return 0;
};

export const intervalsOverlap = (interval, otherInterval) => (
  comparePositions(interval.start, otherInterval.end) < 0 &&
  comparePositions(otherInterval.start, interval.end) < 0
);

export const validateAnnotationInterval = ({
  interval,
  annotations,
  trackId,
  excludeId,
  minimumWidth = 0,
  toValue,
}) => {
  if (comparePositions(interval.start, interval.end) >= 0) {
    return failed('invalid-interval', interval);
  }

  if (toValue) {
    const intervalWidth = toValue(interval.end) - toValue(interval.start);
    const widthTolerance = Number.EPSILON * Math.max(
      1,
      Math.abs(intervalWidth),
      Math.abs(minimumWidth)
    ) * 8;
    if (intervalWidth + widthTolerance < minimumWidth) {
      return failed('minimum-width', interval);
    }
  }

  const collision = relevantAnnotations(annotations, trackId, excludeId)
    .some(annotation => intervalsOverlap(interval, annotationInterval(annotation)));

  if (collision) return failed('collision', interval);

  return { success: true, interval: cloneInterval(interval) };
};

export const clampResizeInterval = ({
  originalInterval,
  proposedInterval,
  direction,
  annotations,
  trackId,
  excludeId,
  minimumWidth = 0,
  toValue,
}) => {
  const neighbors = relevantAnnotations(annotations, trackId, excludeId)
    .map(annotationInterval);
  const clamped = cloneInterval(proposedInterval);

  if (direction === 'right') {
    const boundary = neighbors
      .map(neighbor => neighbor.start)
      .filter(start => comparePositions(start, originalInterval.start) >= 0)
      .reduce((closest, start) => (
        !closest || comparePositions(start, closest) < 0 ? start : closest
      ), null);

    if (boundary && comparePositions(clamped.end, boundary) > 0) {
      clamped.end = clonePosition(boundary);
    }
  } else if (direction === 'left') {
    const boundary = neighbors
      .map(neighbor => neighbor.end)
      .filter(end => comparePositions(end, originalInterval.end) <= 0)
      .reduce((closest, end) => (
        !closest || comparePositions(end, closest) > 0 ? end : closest
      ), null);

    if (boundary && comparePositions(clamped.start, boundary) < 0) {
      clamped.start = clonePosition(boundary);
    }
  }

  if (toValue && toValue(clamped.end) - toValue(clamped.start) < minimumWidth) {
    return failed('minimum-width', originalInterval);
  }

  const validation = validateAnnotationInterval({
    interval: clamped,
    annotations,
    trackId,
    excludeId,
  });

  return validation.success ? validation : failed(validation.reason, originalInterval);
};

export const clampDragInterval = ({
  originalInterval,
  proposedInterval,
  annotations,
  trackId,
  excludeId,
  minimumWidth = 0,
  toValue,
  fromValue,
}) => {
  const neighbors = relevantAnnotations(annotations, trackId, excludeId)
    .map(annotationInterval);
  const duration = toValue(originalInterval.end) - toValue(originalInterval.start);

  if (duration < minimumWidth) return failed('minimum-width', originalInterval);

  const leftBoundary = neighbors
    .map(neighbor => neighbor.end)
    .filter(end => comparePositions(end, originalInterval.start) <= 0)
    .reduce((closest, end) => (
      !closest || comparePositions(end, closest) > 0 ? end : closest
    ), null);
  const rightBoundary = neighbors
    .map(neighbor => neighbor.start)
    .filter(start => comparePositions(start, originalInterval.end) >= 0)
    .reduce((closest, start) => (
      !closest || comparePositions(start, closest) < 0 ? start : closest
    ), null);

  const proposedStart = toValue(proposedInterval.start);
  const minimumStart = leftBoundary ? toValue(leftBoundary) : -Infinity;
  const maximumStart = rightBoundary ? toValue(rightBoundary) - duration : Infinity;

  if (maximumStart < minimumStart) return failed('minimum-width', originalInterval);

  const clampedStart = Math.min(Math.max(proposedStart, minimumStart), maximumStart);
  const clamped = {
    start: clampedStart === proposedStart
      ? clonePosition(proposedInterval.start)
      : fromValue(clampedStart),
    end: fromValue(clampedStart + duration),
  };
  const convertedDuration = toValue(clamped.end) - toValue(clamped.start);
  const durationTolerance = Number.EPSILON * Math.max(1, Math.abs(duration)) * 8;

  if (Math.abs(convertedDuration - duration) > durationTolerance) {
    return failed('duration-change', originalInterval);
  }

  const validation = validateAnnotationInterval({
    interval: clamped,
    annotations,
    trackId,
    excludeId,
    minimumWidth,
    toValue,
  });

  return validation.success ? validation : failed(validation.reason, originalInterval);
};
